import { useMutation } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
} from "firebase/auth";
import {
  saveTokenExpiry,
  clearAuthStorage,
  getTokenExpiry,
  isTokenExpired,
} from "@/lib/session";
import { refreshIdTokenRequest, refreshWithRetry } from "@/lib/authClient";
import { FUNCTIONS_BASE_URL } from "@/lib/config";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// eslint-disable-next-line react-refresh/only-export-components
export const firebaseAuth = getAuth();

// Connect to Firebase Auth Emulator in local development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const IS_LOCAL = API_BASE_URL.includes('localhost') || import.meta.env.DEV;

if (IS_LOCAL) {
  try {
    connectAuthEmulator(firebaseAuth, "http://localhost:9099");
    console.log('✅ Connected to Firebase Auth Emulator on port 9099');
  } catch (error) {
    console.warn('⚠️ Failed to connect to Auth Emulator. Make sure to run: firebase emulators:start');
    console.error(error);
  }
}

type UserInfo = {
  uid: string;
  email: string | null;
  emailVerified: boolean;
}
export type AuthContext = {
  isAuthenticated: boolean;
  sendSignInLink: (email: string) => void;
  signInWithEmailPassword: (email: string, password: string) => Promise<boolean>;
  logout: (func?: () => void) => Promise<void>;
  user: UserInfo | null;
  verifyToken: () => Promise<boolean>;
  refreshIdToken: () => Promise<boolean>;
};

const AuthContext = createContext<AuthContext | null>(null);

// セッションの有効時間（2時間 = 7200000ミリ秒）
export const SESSION_TIMEOUT = 120 * 60000;

// refresh を実際の expiry の何分前に予約するか（プロアクティブ・スケジューリング）
const REFRESH_LEAD_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isAuthenticated = !!user;

  // verify の同時実行を 1 本に dedup するための in-flight Promise
  const verifyInFlightRef = useRef<Promise<boolean> | null>(null);
  // refresh 予約タイマーと、最新の予約関数を保持する ref
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRefreshRef = useRef<() => void>(() => {});

  // トークンの検証
  const verifyToken = async (): Promise<boolean> => {
    if (isLoggingOut) return false;
    const idToken = localStorage.getItem("idToken");
    if (!idToken) return false;
    // 同時実行は 1 本に dedup する
    if (verifyInFlightRef.current) return verifyInFlightRef.current;

    const run = async (): Promise<boolean> => {
      try {
        // auth-relay 経由でトークンを検証（ローカル / 本番で共通）
        console.log("Verifying token with API");
        const response = await fetch(`${FUNCTIONS_BASE_URL}/verifyToken`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ idToken }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // 検証中に logout() / clearAuthStorage() が走った場合、
          // 古いトークンの検証結果で user state を「復活」させない。
          // 呼び出し時点の idToken と現在の値が一致しない場合は stale 扱いで破棄する。
          if (localStorage.getItem("idToken") !== idToken) {
            return false;
          }
          setUser({
            uid: data.uid,
            email: data.email,
            emailVerified: true,
          });
          localStorage.setItem("lastActive", Date.now().toString());
          return true;
        }
        console.warn("Token verification failed:", data.error || "Unknown error");
        return false;
      } catch (error) {
        console.error("Error during token verification:", error);
        return false;
      }
    };

    verifyInFlightRef.current = run().finally(() => {
      verifyInFlightRef.current = null;
    });
    return verifyInFlightRef.current;
  };

  // トークンのリフレッシュ（共通 refresh クライアントへ委譲。dedup 込み。
  // refresh 後の verifyToken 呼び出しは廃止し、1 周期での verify→refresh→verify の
  // 3 連発を解消する）。
  const refreshIdToken = async (): Promise<boolean> => {
    return refreshIdTokenRequest();
  };

  useEffect(() => {
    const initialAuthCheck = async () => {
      if (isLoggingOut) return;

      try {
        const idToken = localStorage.getItem("idToken");
        if (idToken) {
          const isValid = await verifyToken();
          if (!isValid) {
            const refreshed = await refreshIdToken();
            // refresh 成功時は新しいトークンで user state を確定する（初回ブートのみ）。
            // verify が失敗した場合は、トークンを残したまま user=null の半端状態を残さないよう、
            // refresh 失敗時と同じ後始末をする。
            if (refreshed) {
              const verifiedAfterRefresh = await verifyToken();
              if (!verifiedAfterRefresh) {
                clearAuthStorage();
                setUser(null);
              }
            } else {
              clearAuthStorage();
              setUser(null);
            }
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Error during initial auth check:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initialAuthCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggingOut]);

  const logoutMutation = useMutation({
    mutationKey: ["logout"],
    mutationFn: async () => {
      setIsLoggingOut(true);
      try{
        clearAuthStorage();
        setUser(null);
        setTimeout(() => {
          setIsLoggingOut(false);
        }
        , 1000);
        return true;
      } catch (error) {
        console.error("Logout error:", error);
        setIsLoggingOut(false);
        throw error;
      }
    },
  });

  const logout = useCallback(
    async (onSuccessFunc?: () => void) => {
      try {
        await logoutMutation.mutateAsync(undefined);
        // 確実にユーザー状態をリセット
        setUser(null);
        if (onSuccessFunc) {
          onSuccessFunc();
        }
      } catch (error) {
        console.error("Logout failed:", error);
        // エラーが発生しても、安全のためユーザー状態をクリア
        setUser(null);
      }
    },
    [logoutMutation]
  );

  // expiresIn ベースの refresh プロアクティブ・スケジューラ。
  // 60 秒ポーリングを廃止し、expiry の REFRESH_LEAD_MS 前に 1 回だけ refresh を予約する。
  // タイマーコールバックから最新版を呼べるよう ref に保持する。
  scheduleRefreshRef.current = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    const expiresAt = getTokenExpiry();
    if (expiresAt === null) return;
    const delay = Math.max(expiresAt - REFRESH_LEAD_MS - Date.now(), 0);
    refreshTimerRef.current = setTimeout(async () => {
      if (isLoggingOut) return;
      // セッションが既に破棄されていれば何もしない。
      // 401 インターセプターなどが先に clearAuthStorage() を走らせた場合に、
      // ここで再度 logout() を呼ばないようにするためのガード。
      if (
        !localStorage.getItem("idToken") ||
        !localStorage.getItem("refreshToken")
      ) {
        return;
      }
      // 401 インターセプター等の別経路が先に refresh して expiresAt を更新済みの場合、
      // ここでの refresh は不要。現在の expiresAt が REFRESH_LEAD_MS より先なら、
      // 再予約だけして refresh はスキップする。
      const currentExpiresAt = getTokenExpiry();
      if (
        currentExpiresAt !== null &&
        Date.now() < currentExpiresAt - REFRESH_LEAD_MS
      ) {
        scheduleRefreshRef.current();
        return;
      }
      // 無操作タイムアウトを超過していれば refresh せずログアウト
      const lastActive = Number(localStorage.getItem("lastActive"));
      if (!lastActive || Date.now() - lastActive > SESSION_TIMEOUT) {
        await logout();
        return;
      }
      const ok = await refreshWithRetry();
      if (ok) {
        scheduleRefreshRef.current(); // 新しい expiry で再予約
      } else {
        await logout();
      }
    }, delay);
  };

  // 認証済みの間だけ refresh を予約し、未認証になったらタイマーを解除する
  useEffect(() => {
    if (!isAuthenticated) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }
    scheduleRefreshRef.current();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // タブ復帰時に expiry をチェックし、期限切れなら即 refresh（失敗ならログアウト）
  useEffect(() => {
    const onVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      if (!isAuthenticated || isLoggingOut) return;
      // スケジューラと同様、別経路で既に clearAuthStorage() 済みの場合は何もしない。
      if (
        !localStorage.getItem("idToken") ||
        !localStorage.getItem("refreshToken")
      ) {
        return;
      }
      if (isTokenExpired()) {
        const ok = await refreshWithRetry();
        if (ok) {
          scheduleRefreshRef.current();
        } else {
          await logout();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isAuthenticated, isLoggingOut, logout]);

  const sendSignInLinkMutation = useMutation({
    mutationKey: ["sendSignInLink"],
    mutationFn: async (email: string) => {
      try{
        const response = await fetch(`${FUNCTIONS_BASE_URL}/sendSignInLink`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            redirectUrl: window.location.origin + "/auth-callback",
          }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || "Failed to send sign in link");
        }
        return email;
      } catch (error) {
        console.error("Error sending sign in link:", error);
        throw error;
      }
    },
    onSuccess: (email) => {
      localStorage.setItem("emailForSignIn", email);
    },
  });

  const sendSignInLink = useCallback(
    (email: string) => {
      sendSignInLinkMutation.mutate(email);
    },
    [sendSignInLinkMutation]
  );

  const signInWithEmailPasswordMutation = useMutation({
    mutationKey: ["signInWithEmailPassword"],
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      try {
        // auth-relay 経由で認証（ローカル / 本番で共通）
        console.log('🔐 Signing in with Cloud Functions...');
        const response = await fetch(`${FUNCTIONS_BASE_URL}/signInWithEmailPassword`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || "アカウント情報が一致しません");
        }

        localStorage.setItem("idToken", data.idToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem("lastActive", Date.now().toString());
        saveTokenExpiry(data.expiresIn);

        // ユーザー情報を取得するためにトークンを検証
        await verifyToken();
        return true;
      } catch (error) {
        console.error("Error signing in with email and password:", error);
        throw error;
      }
    }
  });

  const signInWithEmailPassword = useCallback(
    async (email: string, password: string) => {
      try {
        await signInWithEmailPasswordMutation.mutateAsync({ email, password });
        return true;
      } catch (error) {
        console.error("Error signing in with email and password:", error);
        return false;
      }
    },
    [signInWithEmailPasswordMutation]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        sendSignInLink,
        signInWithEmailPassword,
        logout,
        verifyToken,
        refreshIdToken,
      }}
    >
      {loading ? <></> : children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
